import { defineStore } from "pinia";
import { PublisherStatus } from "~/interfaces/publisher";
import { ElMessageBox, ElNotification } from "element-plus";
import { ApiKey } from "~/common/API/baseApi";
import PublisherService from "~/services/PublisherService";
import type { BindMessenger } from "~/services/AuthService";
import type { IAddPublisherPaymentsData } from "~/interfaces/publisher/moderation";
import { getUserId } from "~/common/helpers";
import type { IYuriSaveData } from "~/interfaces/publisher/payments";
import { chatApi } from "~/common/API/chatApi";
import { simpleApi } from "~/common/API/simpleApi";
import { jwtDecode } from "jwt-decode";

// let checkTgAuthLink = '/user/auth/telegram/get-access-token';
// let checkTgAuthLink = '/user/bot/publisher/login';
let emailAuthLink = "/user/publisher/login";

let timeoutValueToCheckTg = 60000;
let intervalValueToCheckTg = 3000;

export const usePublisherStore = defineStore("publisher", () => {
  // ВАЖНО: НЕ вызываем useRuntimeConfig() здесь - это вызовет ошибку на SSR
  // Вызываем его lazy внутри функций где нужен

  const id = ref<number | string>("");
  const name = ref<string>("");
  const companyName = ref<string>("");
  const phone = ref<string>("");
  const avatar = ref<string>("");
  const balance = ref<number>(0);
  // Доступная к выводу сумма паблишера. Обновляется по сокету PublisherWalletWithdrawal.
  const withdrawalAvailable = ref<number | null>(null);
  const dialogCount = ref<number>(0);
  const walletData = ref<number>(0);
  const telegramId = ref<string>("");
  const veryfyEmail = ref<{
    email: string;
    time: Date;
    attempt: number;
  } | null>(null);

  const setAttempts = () => {
    if (!veryfyEmail.value) return;
    veryfyEmail.value = {
      email: veryfyEmail.value?.email,
      time: veryfyEmail.value?.time,
      attempt: veryfyEmail.value?.attempt + 1,
    }
  };

  const isDetails = computed(() => {
    return walletData.value > 0;
  });

  let status = ref<PublisherStatus>(PublisherStatus.NOT_AUTHORIZED);
  let telegram: Ref<string> = ref("");
  // Регистрация возможна и через MAX — тогда телеграма у пользователя нет
  const max: Ref<string> = ref("");
  // let telegramId: Ref<string | null> = ref(null);
  let email: Ref<string | null> = ref(null);
  let emailConfirmed: Ref<boolean> = ref(false);
  let authKey: Ref<string> = ref("");
  let authorized: Ref<string> = ref("");
  // Через какого бота идёт текущая авторизация: у мессенджеров свои ручки
  const authMessenger: Ref<BindMessenger> = ref("telegram");

  const roles: Ref<number[]> = ref([]);
  // Бэк отдаёт роли в поле role; roles — на случай старого формата ответа
  const rolesOf = (data: any): number[] | undefined => data?.role ?? data?.roles;

  let intervalIdToTgCheck: Ref<number> = ref(0);
  let timeoutIdToTgCheck: Ref<number> = ref(0);

  let loginDialogVisible: Ref<boolean> = ref(false);

  // Взял из стока, зачем пока не понятно
  function setStatus(data: PublisherStatus) {
    status.value = data;
  }

  const userObject = computed(() => {
    return {
      name: name.value,
      companyName: "",
      telegram: telegram.value,
      email: email.value,
      phone: "",
      avatar: avatar.value,
      telegramId: telegramId.value,
    };
  });

  function getUser() {
    return {
      name: name.value,
      companyName: "",
      telegram: telegram.value,
      email: email.value,
      phone: "",
      avatar: avatar.value,
      telegramId: telegramId.value,
    };
  }

  // Для работы с каналами нужны и почта, и один из мессенджеров.
  // Каким из них пользователь регистрировался — неважно
  const hasMessenger = computed(() => {
    const tg = telegram.value;
    return (!!tg && tg.length >= 3 && tg !== "undefined") || !!max.value;
  });

  const currentStatus = computed(() => {
    // return 3;
    if (!email.value && !emailConfirmed.value && !authorized.value) {
      return 0; // Все поля пустые
    } else if (!hasMessenger.value) {
      return 1; // Авторизация в боте: Telegram или MAX
    } else if ((!email.value || email.value === "null") && !emailConfirmed.value) {
      return 2; // Не введена почта и не подтверждена
    } else if (email.value && !emailConfirmed.value) {
      return 3; // Введена почта, но не подтверждена
    } else if (email.value && emailConfirmed.value) {
      return 4; // Введены и подтверждены все данные
    } else {
      return 0;
    }
  });

  function setEmail(data: string) {
    email.value = data;
  }

  function getUserChannels() {
    return [];
  }

  function resetAuthKey() {
    name.value = "";
    companyName.value = "";
    phone.value = "";
    authKey.value = "";
    authorized.value = "";
    balance.value = 0;
    avatar.value = "";
    id.value = "";
    email.value = "";
    emailConfirmed.value = false;
    telegram.value = "";
    max.value = "";
    telegramId.value = "";
    roles.value = [];
    walletData.value = 0;
    useCookie("accessTokenSJ").value = "";
    useCookie("authorizedP").value = "";
    setLS();
  } // resetAuthKey

  function setAuthorized(key: string, auth: string, id_v: string) {
    authKey.value = key;
    authorized.value = auth;
    id.value = +id_v;
    setLS();
  } // setAuthorized

  function setLS(refresh: boolean = false) {
    console.log("setLS In", telegramId.value);
    localStorage.setItem("usePublisherStore__authKey", authKey.value);
    if (!refresh) localStorage.setItem("usePublisherStore__authorized", authorized.value);
    localStorage.setItem("usePublisherStore__id", id.value.toString());
    localStorage.setItem("usePublisherStore__name", name.value);
    localStorage.setItem("usePublisherStore__email", email?.value || "");
    localStorage.setItem("usePublisherStore__avatar", avatar.value);
    localStorage.setItem("usePublisherStore__walletData", walletData?.value?.toString() || "0");
    localStorage.setItem(
      "usePublisherStore__emailConfirmed",
      String(emailConfirmed.value || false)
    );
    localStorage.setItem("usePublisherStore__telegram", telegram.value);
    localStorage.setItem("usePublisherStore__max", max.value);
    localStorage.setItem("usePublisherStore__telegramId", telegramId.value || "");
    localStorage.setItem("usePublisherStore__roles", JSON.stringify(roles.value));
    if (!refresh) useCookie("authorizedP").value = authorized.value;
    if (!refresh) useCookie("accessToken").value = authorized.value;
  } // setLS

  async function simpleRequestAuthKey(
    dop: boolean = false,
    messenger: BindMessenger = "telegram",
    botWindow: Window | null = null
  ) {
    console.log("requestAuthKey1");
    const result = await PublisherService.requestAuthKey(messenger);
    console.log("simpleRequestAuthKey", result);

    if (!result.authKey) return;

    // Мессенджер запоминаем: поллинг check-auth ходит в его же ручку
    authMessenger.value = messenger;
    authKey.value = result.authKey;
    localStorage.setItem("usePublisherStore__authKey", authKey.value);
    console.log("dop", authKey.value);
    if (dop) return;

    const config = useRuntimeConfig().public;
    const prefix =
      messenger === "max"
        ? config.RESOLVE_MAX_AUTHORIZE_PREFIX_PUBLISHER
        : config.RESOLVE_TELEGRAM_AUTHORIZE_PREFIX_PUBLISHER;
    const url = `${prefix}${authKey.value}`;
    // Бот MAX — веб-страница: в текущей вкладке она затёрла бы кабинет.
    // Окно приходит из компонента, открытое синхронно по клику, иначе
    // браузер заблокирует его как всплывающее.
    if (botWindow) botWindow.location.href = url;
    else window.open(url, messenger === "max" ? "_blank" : "_self");
    // Ждем 1 секунду перед выполнением
    setTimeout(async () => {
      const user = await PublisherService.getUser();
      console.log("USER", user);
      if (user.telegram) {
        telegram.value = user.telegram;
        setLS();
        ElNotification({
          title: "Telegram успешно привязан",
          message: "Спасибо!",
          type: "success",
        });
      } else {
        ElNotification({
          title: "Ошибка привязки Telegram",
          message: "попробуйте чуть позже или свяжитесь с администратором...",
          type: "error",
        });
      }
    }, 2000);
    return;
  }

  async function requestAuthKey(messenger: BindMessenger = "telegram") {
    const usePublisherStore__authorized = localStorage.getItem("usePublisherStore__authorized");
    if (usePublisherStore__authorized) {
      // Проверяем, не истёк ли JWT токен
      try {
        const decoded = jwtDecode<{ exp?: number }>(usePublisherStore__authorized);
        if (typeof decoded.exp === "number" && decoded.exp < Date.now() / 1000) {
          // Токен истёк — очищаем данные и перенаправляем на авторизацию
          resetAuthKey();
          useCookie("authorizedP").value = "";
          navigateTo("/publisher");
          return;
        }
      } catch {
        // Если токен невалидный — тоже очищаем
        resetAuthKey();
        useCookie("authorizedP").value = "";
        navigateTo("/publisher");
        return;
      }
      authorized.value = usePublisherStore__authorized;
      useCookie("authorizedP").value = usePublisherStore__authorized;
    }
    const usePublisherStore__authKey = localStorage.getItem("usePublisherStore__authKey");
    if (usePublisherStore__authKey) {
      authKey.value = usePublisherStore__authKey;
    }
    const usePublisherStore__telegram = localStorage.getItem("usePublisherStore__telegram");
    if (usePublisherStore__telegram) {
      telegram.value = usePublisherStore__telegram;
    }
    const usePublisherStore__max = localStorage.getItem("usePublisherStore__max");
    if (usePublisherStore__max) {
      max.value = usePublisherStore__max;
    }
    const usePublisherStore__telegramId = localStorage.getItem("usePublisherStore__telegramId");
    if (usePublisherStore__telegramId) {
      telegramId.value = usePublisherStore__telegramId;
    }
    const usePublisherStore__email = localStorage.getItem("usePublisherStore__email");
    if (usePublisherStore__email) {
      email.value = usePublisherStore__email;
    }
    const usePublisherStore__name = localStorage.getItem("usePublisherStore__name");
    if (usePublisherStore__name) {
      name.value = usePublisherStore__name;
    }
    const usePublisherStore__roles = localStorage.getItem("usePublisherStore__roles");
    if (usePublisherStore__roles) {
      roles.value = JSON.parse(usePublisherStore__roles);
    }
    const usePublisherStore__emailConfirmed = localStorage.getItem(
      "usePublisherStore__emailConfirmed"
    );
    if (usePublisherStore__emailConfirmed) {
      emailConfirmed.value = usePublisherStore__emailConfirmed === "true";
    }
    const usePublisherStore__id = localStorage.getItem("usePublisherStore__id");
    if (usePublisherStore__id) {
      id.value = usePublisherStore__id;
    }
    const usePublisherStore__avatar = localStorage.getItem("usePublisherStore__avatar");
    if (usePublisherStore__avatar) {
      avatar.value = usePublisherStore__avatar;
    }
    const usePublisherStore__walletData = localStorage.getItem("usePublisherStore__walletData");
    if (usePublisherStore__walletData) {
      walletData.value = +usePublisherStore__walletData;
    }
    if (authorized.value) {
      return;
    }
    // Ключ выдаётся под конкретного бота: сменили мессенджер — берём новый
    if (authMessenger.value !== messenger) {
      authKey.value = "";
    }
    authMessenger.value = messenger;
    if (authKey.value && authKey.value !== "undefined") {
      return;
    }
    await $fetch(`/user/${messenger}/bot/generate-auth-key`, {
      baseURL: useRuntimeConfig().public.LIGHT_API_ORIGIN,
      method: "POST",
      onRequest({ request, options }) {
        // Set the request headers
        // options.headers = options.headers || {}
        // options.headers.authorization = '...'
      },
      onRequestError({ request, options, error }) {
        // Handle the request errors
      },
      onResponse({ request, response, options }) {
        // Process the response data
        authKey.value = response._data.authKey;
        localStorage.setItem("usePublisherStore__authKey", authKey.value);
      },
      onResponseError({ request, response, options }) {
        // Handle the response errors
      },
    });
  } // requestAuthKey

  async function startMessengerAuthorize(
    messenger: BindMessenger = "telegram",
    botWindow: Window | null = null
  ): Promise<boolean> {
    authMessenger.value = messenger;
    return startTelegramAuthorize(botWindow);
  }

  async function startTelegramAuthorize(botWindow: Window | null = null): Promise<boolean> {
    try {
      // await ElMessageBox.confirm(
      //   "Вход через Telegram",
      //   "Перейдите в Telegram и нажмите start в боте",
      //   {
      //     showCancelButton: false,
      //     confirmButtonText: "OK",
      //   }
      // );
      if (!authKey.value) {
        console.log("authKey.value not found");
        return false;
      }
      const config = useRuntimeConfig().public;
      const prefix =
        authMessenger.value === "max"
          ? config.RESOLVE_MAX_AUTHORIZE_PREFIX_PUBLISHER
          : config.RESOLVE_TELEGRAM_AUTHORIZE_PREFIX_PUBLISHER;
      const url = `${prefix}${authKey.value}`;

      if (botWindow) botWindow.location.href = url;
      else window.open(url, authMessenger.value === "max" ? "_blank" : "_self");
      loginDialogVisible.value = false;
      return await checkTelegramAuthorize();
    } catch {
      return false;
    }
  } // startTelegramAuthorize

  function checkTelegramAuthorize(): Promise<boolean> {
    return new Promise((resolve) => {
      // Очищаем текущие интервалы и таймауты перед запуском
      clearInterval(intervalIdToTgCheck.value);
      clearTimeout(timeoutIdToTgCheck.value);

      // Устанавливаем таймаут для завершения интервала через указанное время
      timeoutIdToTgCheck.value = window.setTimeout(() => {
        clearInterval(intervalIdToTgCheck.value);
        resolve(false); // Возвращаем false, если истекло время таймаута
      }, timeoutValueToCheckTg);

      // Устанавливаем интервал для проверки авторизации
      intervalIdToTgCheck.value = window.setInterval(() => {
        if (authorized.value && id.value && telegram.value) {
          clearInterval(intervalIdToTgCheck.value);
          clearTimeout(timeoutIdToTgCheck.value);
          resolve(true); // Возвращаем true, если данные успешно получены
          return;
        }

        // Выполняем запрос для проверки статуса авторизации.
        // Ручка зависит от мессенджера, через которого начали авторизацию.
        let endpoint = `/user/${authMessenger.value}/bot/check-auth`;
        if (authorized.value) {
          endpoint = `/user/${authMessenger.value}/bot/user-bind-start`;
        }

        $fetch(endpoint, {
          baseURL: useRuntimeConfig().public.LIGHT_API_ORIGIN,
          method: "GET",
          query: {
            authKey: authKey.value,
          },
          onRequest({ request, options }) { },
          onRequestError({ request, options, error }) { },
          onResponse({ request, response, options }) {
            // console.log("!!!!!!!!!!!!", response);
            if (response.status === 500) {
              ElNotification({
                title: "Ошибка авторизации",
                message: "попробуйте чуть позже или свяжитесь с администратором...",
                type: "warning",
              });
              clearInterval(intervalIdToTgCheck.value);
              clearTimeout(timeoutIdToTgCheck.value);
            }
            console.log("response._data", response._data);
            // Если доступен токен авторизации, сохраняем его и останавливаем проверку
            if (response._data.accessToken && response._data.id) {
              // console.log("response._data", response._data);
              authorized.value = response._data.accessToken;
              id.value = response._data.id;
              telegram.value = response._data.telegram;
              max.value = response._data.max || "";
              telegramId.value = response._data.telegramId;

              ApiKey.set(response._data.accessToken);
              if (response._data.email && !response._data.emailIsFake) {
                email.value = response._data.email;
                emailConfirmed.value = true;
              }
              const userRoles = rolesOf(response._data);
              if (userRoles) roles.value = userRoles;
              if (response._data.walletInfo?.publisherWallet?.type) {
                walletData.value = response._data.walletInfo.publisherWallet.type;
              }
              name.value = response._data.name || "";
              companyName.value = response._data.companyName || "";
              phone.value = response._data.phone || "";
              avatar.value = response._data.avatar || "";
              ElNotification({
                title: "Успешная авторизация через Telegram",
                message: "Спасибо!",
                type: "success",
              });
              console.log("setLS", telegramId.value);
              setLS();

              clearInterval(intervalIdToTgCheck.value);
              clearTimeout(timeoutIdToTgCheck.value);
              status.value = PublisherStatus.AUTHORIZED_IN_BOT;
              reloadNuxtApp({ persistState: true });
              resolve(true); // Возвращаем true при успешной авторизации
            } else {
              ElNotification({
                title: "Ожидание авторизации",
                message: "Ждём подтверждения от бота...",
                type: "warning",
              });
            }
          },
          onResponseError({ request, response, options }) { },
        });
      }, intervalValueToCheckTg);
    });
  }

  async function getUserWalletData() {
    const res = await PublisherService.getPublisherPaymentsData();
    console.log("getUserWalletData", res);
    return res;
  }

  function startEmailAuthorize({ emaill, passwords }: { emaill: string; passwords: string }) {
    return new Promise((resolve) => {
      $fetch(emailAuthLink, {
        baseURL: useRuntimeConfig().public.LIGHT_API_ORIGIN,
        method: "POST",
        body: {
          username: emaill,
          password: passwords,
        },
        onResponseError({ request, response, options }) {
          resolve(false);
          ElNotification({
            title: "Ошибка авторизации",
            message: "Не верный логин или пароль...",
            type: "warning",
          });
        },
        onResponse({ request, response, options }) {
          if (response._data.accessToken && response._data.id) {
            console.log("response._data", response._data);
            // authorized.value = response._data.accessToken;
            // ApiKey.set(response._data.accessToken);
            // id.value = response._data.id;

            // email.value = emaill.toString();
            // emailConfirmed.value = true;
            authorized.value = response._data.accessToken;
            id.value = response._data.id;
            telegram.value = response._data.telegram;
            max.value = response._data.max || "";
            telegramId.value = response._data.telegramId;

            ApiKey.set(response._data.accessToken);
            if (response._data.email && !response._data.emailIsFake) {
              email.value = response._data.email;
              emailConfirmed.value = true;
            }
            const userRoles = rolesOf(response._data);
            if (userRoles) roles.value = userRoles;
            if (response._data.walletInfo?.publisherWallet?.type) {
              walletData.value = response._data.walletInfo.publisherWallet.type;
            }
            name.value = response._data.name || "";
            companyName.value = response._data.companyName || "";
            phone.value = response._data.phone || "";
            avatar.value = response._data.avatar || "";

            ElNotification({
              title: "Успешная авторизация через Email",
              message: "Спасибо!",
              type: "success",
            });
            console.log("setLS", email.value, id.value, telegram.value, authorized.value, roles.value, name.value, companyName.value, phone.value, avatar.value);
            setLS();
            status.value = PublisherStatus.VERIFICATED_EMAIL;
            resolve(true); // Возвращаем true при успешной авторизации
          }
        },
      });
    });
  }

  async function setPublisherEmail() {
    if (email.value && id.value) {
      try {
        const res = await PublisherService.setPublisherEmail(email.value, +id.value);
        ElNotification({
          title: "Успешная отправка запроса",
          message: "Спасибо!",
          type: "success",
        });
      } catch (err) {
        ElNotification({
          title: "Ошибка отправки письма",
          message: "попробуйте чуть позже или свяжитесь с администратором...",
          type: "warning",
        });
      }
    } else {
      console.error("email or id not found");
    }
  }

  async function confirmPublisherEmail(code: string) {
    const res = await PublisherService.publisherEmailConfirmation(code);
    if (res && res.email) {
      emailConfirmed.value = true;
      email.value = res.email;
      setLS();
      ElNotification({
        title: "Успешная подтверждение",
        message: "Спасибо!",
        type: "success",
      });
    } else {
      ElNotification({
        title: "Ошибка подтверждения",
        message: "попробуйте чуть позже или свяжитесь с администратором...",
        type: "warning",
      });
    }
  }

  interface ISaveData {
    telegram?: string;
    email?: string;
    name?: string;
    newPassword?: string;
    avatar?: string;
  }
  const updateUser = async (data: any) => {
    let saveData: ISaveData = {};

    Object.keys(data).forEach((key) => {
      if (data[key as keyof typeof data] !== "") {
        saveData[key as keyof ISaveData] = data[key as keyof ISaveData];
      }
    });

    if (saveData.avatar && saveData.avatar === avatar.value) {
      delete saveData.avatar;
    }
    // 
    //  if (saveData.telegram && saveData.telegram === telegram.value) {
    //   delete saveData.telegram;
    // }

    // if (saveData.email && saveData.email === email.value) {
    //   delete saveData.email;
    // }

    if (saveData.name && saveData.name === name.value) {
      delete saveData.name;
    }

    // if (saveData.newPassword) {
    //   try {
    //     const passwordResponse = await PublisherService.updateUserPassword(
    //       +id.value,
    //       saveData.newPassword
    //     );
    //     if (passwordResponse) {
    //       ElNotification({
    //         title: "Пароль обновлен",
    //         message: "Спасибо!",
    //         type: "success",
    //       });
    //     }
    //   } catch (err) {
    //     ElNotification({
    //       title: "Ошибка обновления пароля",
    //       message: "попробуйте чуть позже или свяжитесь с администратором...",
    //       type: "warning",
    //     });
    //   }
    //   delete saveData.newPassword;
    // }

    if (Object.keys(saveData).length === 0) return;
    console.log("saveData", saveData, Object.keys(saveData).length);
    const res = await PublisherService.updateUser({ ...saveData, id: +id.value });
    if (res) {
      ElNotification({
        title: "Успешное обновление",
        message: "Спасибо!",
        type: "success",
      });
      Object.keys(saveData).forEach((key) => {
        // const saveKey = key as keyof ISaveData;
        if (key === "email") {
          email.value = saveData[key as keyof ISaveData] || null;
        } else if (key === "name") {
          name.value = saveData[key as keyof ISaveData] || "";
        } else if (key === "telegram") {
          telegram.value = saveData[key as keyof ISaveData] || "";
        } else if (key === "avatar") {
          avatar.value = saveData[key as keyof ISaveData] || "";
        }
      });
      setLS();
    }
    return [];
  };

  async function addPublisherPaymentsData(payload: IAddPublisherPaymentsData) {
    console.log("pl", payload);
    // return;
    // Удаляем пустые поля из payload
    const cleanPayload = Object.fromEntries(
      Object.entries(payload).filter(
        ([_, value]) =>
          value !== null &&
          value !== undefined &&
          value !== "" &&
          (typeof value === "object" ? Object.keys(value).length > 0 : true)
      )
    );

    if (cleanPayload.type === "1" || cleanPayload.type === "2") {
      delete cleanPayload.secondCountry;
    }

    // @ts-ignore
    const res = await PublisherService.addPublisherPaymentsData({ ...cleanPayload, id: +id.value });
    console.log("addPublisherPaymentsData res", res);
    return res;
  }

  async function addPublisherPaymentsDataYuri(payload: IYuriSaveData) {
    try {
      const res = await PublisherService.addPublisherPaymentsDataYuri({ ...payload });
      console.log("addPublisherPaymentsDataYuri res", res);
      return res;
    } catch (err: any) {
      if (err.message) {
        ElNotification({
          title: "Ошибка добавления данных",
          message: err.message,
          type: "warning",
        });
        return [];
      }
    }
  }

  async function getPublisherPaymentsData() {
    if (walletData.value < 1) {
      // console.log("walletData.value 0", walletData.value);
      return;
    }

    const result = await PublisherService.getPublisherPaymentsData();
    return result;
  }

  async function updatePublisherPaymentsData(payload: { id: number; phoneNumber: string }) {
    try {
      const res = await PublisherService.updatePublisherPaymentsData(payload);
      ElNotification({
        title: "Успешное обновление",
        message: "Спасибо!",
        type: "success",
      });
      return res;
    } catch (err) {
      console.error("updatePublisherPaymentsData: Error:", err);
    }
  }

  async function logout() {
    resetAuthKey();
    localStorage.removeItem("useUserStore__authKey");
    localStorage.removeItem("useUserStore__authorized");
    localStorage.removeItem("useUserStore__id");
  }

  async function getBalance() {
    const res: any = await getPublisherPaymentsData();
    balance.value = res?.balance || 0;
  }

  async function paymentAction() {
    return await PublisherService.paymentAction();
  }

  async function addNominalAccountCard(cardNumber: string) {
    return await PublisherService.addNominalAccountCard(cardNumber);
  }

  async function getDialogCount() {
    const res: any = await PublisherService.getDialogCount();
    dialogCount.value = res?.notRead || 0;
  }

  async function authFromAdvertiser(token: string | null | undefined) {
    if (!token) return null;
    useCookie("authorizedP").value = token;
    authorized.value = token;
    const data: any = await simpleApi("/user/");
    if (!data.role.includes(3)) {
      ElNotification({
        title: "Ошибка авторизации",
        message: "Вы ещё не авторизовались как паблишер",
        type: "warning",
      });
      await logout();
      useRouter().push("/publisher");
      return null;
    }
    // console.log("data", data);
    if (data) {


      authorized.value = token;
      id.value = data.id;
      telegram.value = data.telegram;
      telegramId.value = data.telegramId;

      ApiKey.set(token);
      if (data.email && !data.emailIsFake) {
        email.value = data.email;
        emailConfirmed.value = true;
      }

      roles.value = rolesOf(data) ?? [];

      name.value = data.name || "";
      companyName.value = data.companyName || "";
      phone.value = data.phone || "";
      avatar.value = data.avatar || "";
      setLS();
      const res: any = await getUserWalletData();
      if (res) {
        balance.value = res.balance || 0;
        walletData.value = res.type || 0;
      }
      // console.log("res", res);
    }
    return data;
  }

  async function refreshUserInfo() {
    const data: any = await PublisherService.getUser();
    console.log("getUserInfo", data);
    avatar.value = data.avatar || "";
    telegram.value = data.telegram || "";
    max.value = data.max || "";
    telegramId.value = data.telegramId || "";
    // Фейковый адрес бэк выдаёт сам при регистрации через мессенджер —
    // для пользователя почты нет, её ещё предстоит указать
    email.value = data.emailIsFake ? "" : data.email || "";
    emailConfirmed.value = !data.emailIsFake;
    id.value = data.id || "";
    name.value = data.name || "";
    roles.value = rolesOf(data) ?? [];
    setLS(true);
  }

  async function setVerifyEmail(data: { email: string; time: Date; attempt: number } | null) {
    veryfyEmail.value = data;
  }

  async function requestConfirmCode(newEmail: string, password: string) {
    try {
      const res: any = await PublisherService.requestConfirmCode(+id.value, newEmail, password);
      console.log('requestConfirmCode res', res)
      if (!res) return false;
      return true;
    } catch (e) {

      ElNotification({
        title: 'Ошибка',
        message: "Попробуйте чуть позже",
        type: "error",
      })
      console.log("Too many attemps")
      email.value = "";
      return false;
    }
  }

  async function sendConfirmCode(code: number) {
    const res: any = await PublisherService.sendConfirmCode(code);
    if (!res) return false;
    email.value = res.email;
    emailConfirmed.value = true;
    setLS(true);
    return true;
  }

  async function resetPassword(email: string) {
    try {
      const res = await PublisherService.simpleREsetPassword(email);
      ElNotification({
        title: "Пароль успешно сброшен!",
        message: "Новый пароль отправлен на вашу почту",
        type: "success",
      });
    } catch (e: any) {
      ElNotification({
        title: 'Ошибка',
        message: "попробуйте чуть позже или свяжитесь с администратором...",
        type: "warning",
      })
    }

  }

  async function sendChangeConfirmCode(code: number) {
    const res: any = await PublisherService.sendChangeConfirmCode(code);
    if (!res) return null;
    email.value = res.email;
    emailConfirmed.value = true;
    setLS(true);
    return res;
  }

  function clearVerifyEmail() {
    emailConfirmed.value = false;
    useCookie("possibleResend").value = null;
    email.value = '';
  }

  watch(id, async () => {
    if (id.value) {
      await getBalance();
    }
  });

  onMounted(async () => {
    if (!id.value || id.value === "") {
      const token = useCookie("authorizedP").value;
      if (token) {
        const newId = getUserId();
        if (newId) id.value = newId;
      } else {
        const sjToken = useCookie("accessTokenSJ").value;
        if (sjToken) {
          await authFromAdvertiser(sjToken);
        }
      }
    }
  });

  return {
    id,
    name,
    status,
    telegram,
    telegramId,
    avatar,
    email,
    emailConfirmed,
    authKey,
    balance,
    withdrawalAvailable,
    authorized,
    loginDialogVisible,
    intervalIdToTgCheck,
    timeoutIdToTgCheck,
    currentStatus,
    hasMessenger,
    max,
    roles,
    userObject,
    setLS,
    getUser,
    simpleRequestAuthKey,
    requestAuthKey,
    resetAuthKey,
    setAuthorized,
    startTelegramAuthorize,
    startMessengerAuthorize,
    checkTelegramAuthorize,
    setStatus,
    setEmail,
    getUserChannels,
    startEmailAuthorize,
    logout,
    setPublisherEmail,
    confirmPublisherEmail,
    updateUser,
    addPublisherPaymentsData,
    getPublisherPaymentsData,
    updatePublisherPaymentsData,
    paymentAction,
    addNominalAccountCard,
    addPublisherPaymentsDataYuri,
    dialogCount,
    authFromAdvertiser,
    walletData,
    isDetails,
    getUserWalletData,
    refreshUserInfo,
    veryfyEmail,
    setVerifyEmail,
    requestConfirmCode,
    sendConfirmCode,
    setAttempts,
    clearVerifyEmail,
    sendChangeConfirmCode,
    resetPassword,
    getDialogCount,
  };
});
